/**
 * 학생 채점 현황 페이지
 * - 학생 선택
 * - 문제집 선택
 * - 페이지별 결과 카드 표시
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Select, Typography, Row, Col, Spin, Alert, Button, Radio, Space, Input, message, Modal, Tabs, Tag } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserOutlined, BookOutlined, DeleteOutlined, ExclamationCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { userService } from '../../services/userService';
import { workbookService } from '../../services/workbookService';
import { assignmentService } from '../../services/assignmentService';
import { getStudentResults, getRecentSubmissions, allowRetryBulk } from '../../services/gradingService';
import { useAuthStore } from '../../store/authStore';
import { formatKoreanDateTime } from '../../utils/timeUtils';
import type { User } from '../../types/user';
import type { Workbook } from '../../types/workbook';
import type { Assignment } from '../../types/assignment';
import type { StudentResult, PageResult, RecentPageSubmission } from '../../types/grading';

const { Title, Text } = Typography;
const { Option } = Select;

export const StudentGradingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuthStore();

  // URL 쿼리 파라미터에서 초기값 가져오기
  const studentIdFromUrl = searchParams.get('student');
  const workbookFromUrl = searchParams.get('workbook');

  // 상태 관리 - 탭 (URL에 파라미터 있으면 학생별 조회 탭으로 시작)
  const initialTab = (studentIdFromUrl || workbookFromUrl) ? 'search' : 'recent';
  const [activeTab, setActiveTab] = useState<'recent' | 'search'>(initialTab);

  // 상태 관리 - 최근 제출
  const [recentSubmissions, setRecentSubmissions] = useState<RecentPageSubmission[]>([]);
  const [recentFilter, setRecentFilter] = useState<'needs_action' | 'all' | 'completed'>('needs_action');
  const [recentDays, setRecentDays] = useState<number>(2); // 조회 기간 (기본 2일)
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState<string | null>(null);

  // 상태 관리 - 학생별 조회
  const [students, setStudents] = useState<User[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(studentIdFromUrl);
  const [selectedWorkbook, setSelectedWorkbook] = useState<string | null>(workbookFromUrl);
  const [result, setResult] = useState<StudentResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'needs_attention' | 'all'>('needs_attention');
  const [pageSearch, setPageSearch] = useState<string>('');

  /**
   * 최근 제출 목록 조회
   * useCallback으로 메모이제이션하여 불필요한 재생성 방지
   */
  const fetchRecentSubmissions = useCallback(async () => {
    try {
      setRecentLoading(true);
      setRecentError(null);

      const data = await getRecentSubmissions(recentFilter, recentDays);
      setRecentSubmissions(data.submissions);
    } catch (err: any) {
      console.error('최근 제출 조회 실패:', err);
      setRecentError(err.response?.data?.detail || '최근 제출 조회에 실패했습니다');
    } finally {
      setRecentLoading(false);
    }
  }, [recentFilter, recentDays]);

  /**
   * URL 파라미터 변경 시 상태 동기화
   * URL에서 student/workbook 파라미터가 변경되면 상태도 업데이트
   */
  useEffect(() => {
    const studentIdFromUrl = searchParams.get('student');
    const workbookFromUrl = searchParams.get('workbook');

    // URL 파라미터가 있고 현재 상태와 다르면 업데이트
    if (studentIdFromUrl && studentIdFromUrl !== selectedStudentId) {
      setSelectedStudentId(studentIdFromUrl);
      // 학생이 변경되면 이전 결과 즉시 초기화 (깜빡임 방지)
      setResult(null);
    }

    if (workbookFromUrl && workbookFromUrl !== selectedWorkbook) {
      setSelectedWorkbook(workbookFromUrl);
      // 문제집이 변경되면 이전 결과 즉시 초기화
      setResult(null);
    }

    // URL에 파라미터가 있으면 학생별 조회 탭으로 전환
    if (studentIdFromUrl || workbookFromUrl) {
      setActiveTab('search');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /**
   * 최근 제출 탭 활성화 시에만 데이터 로드 (Lazy Loading)
   * fetchRecentSubmissions가 메모이제이션되어 recentFilter 변경 시에만 재생성됨
   */
  useEffect(() => {
    if (activeTab === 'recent') {
      fetchRecentSubmissions();
    }
  }, [fetchRecentSubmissions, activeTab]);

  /**
   * 학생 목록 조회 (학생별 조회 탭에서만 필요)
   * 강사인 경우: 담당 학생만 조회
   * 그룹 관리자인 경우: 그룹 내 모든 학생 조회
   */
  useEffect(() => {
    // 학생별 조회 탭에서만 학생 목록 로드 (Lazy Loading)
    if (activeTab !== 'search') {
      return;
    }

    const fetchStudents = async () => {
      try {
        // 강사인 경우 담당 학생만 조회
        if (currentUser?.role === 'instructor') {
          const myStudents = await userService.getMyStudents();
          setStudents(myStudents);
        } else {
          // 그룹 관리자 또는 관리자는 모든 학생 조회
          const response = await userService.getUsers({ role: 'student', page_size: 100 });
          setStudents(response.users);
        }
      } catch (err: any) {
        console.error('학생 목록 조회 실패:', err);
      }
    };

    fetchStudents();
  }, [currentUser, activeTab]);

  /**
   * 학생 선택 시 배정된 문제집 목록 조회
   */
  useEffect(() => {
    if (!selectedStudentId) {
      setAssignments([]);
      setSelectedWorkbook(null);
      return;
    }

    const fetchAssignments = async () => {
      try {
        const data = await assignmentService.getStudentAssignments(selectedStudentId);
        setAssignments(data);
      } catch (err: any) {
        console.error('배정 목록 조회 실패:', err);
      }
    };

    fetchAssignments();
  }, [selectedStudentId]);

  /**
   * 학생 결과 조회
   */
  useEffect(() => {
    if (!selectedStudentId || !selectedWorkbook) {
      setResult(null);
      return;
    }

    const fetchResult = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getStudentResults(selectedStudentId, selectedWorkbook);
        setResult(data);
      } catch (err: any) {
        console.error('학생 결과 조회 실패:', err);
        setError(err.response?.data?.detail || '결과 조회에 실패했습니다');
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [selectedStudentId, selectedWorkbook]);

  /**
   * 페이지 필터링 및 정렬
   */
  const getFilteredAndSortedPages = (): PageResult[] => {
    if (!result) return [];

    let filtered = result.pages;

    // 필터 적용
    if (filter === 'needs_attention') {
      filtered = result.pages.filter(
        (page) =>
          page.wrong_problems > 0 ||
          page.pending_problems > 0 ||
          (page.not_submitted_problem_numbers && page.not_submitted_problem_numbers.length > 0)
          // 오답, 채점 대기, 미제출 문제가 있는 페이지만 표시
      );
    }

    // 페이지 검색 필터
    if (pageSearch.trim()) {
      const searchNumber = parseInt(pageSearch.trim());
      if (!isNaN(searchNumber)) {
        filtered = filtered.filter((page) => page.page === searchNumber);
      }
    }

    // 우선순위 정렬
    return filtered.sort((a, b) => {
      // 100% 완료된 페이지 확인 (오답 0 + 채점 대기 0 + 미제출 0)
      const aHasNotSubmitted = a.not_submitted_problem_numbers && a.not_submitted_problem_numbers.length > 0;
      const bHasNotSubmitted = b.not_submitted_problem_numbers && b.not_submitted_problem_numbers.length > 0;
      const aIsComplete = a.wrong_problems === 0 && a.pending_problems === 0 && !aHasNotSubmitted;
      const bIsComplete = b.wrong_problems === 0 && b.pending_problems === 0 && !bHasNotSubmitted;

      // 전체 보기일 때: 100% 완료는 뒤로
      if (filter === 'all') {
        if (!aIsComplete && bIsComplete) return -1;
        if (aIsComplete && !bIsComplete) return 1;
      }

      // 1. 재채점 허락됨 우선
      if (a.retry_allowed && !b.retry_allowed) return -1;
      if (!a.retry_allowed && b.retry_allowed) return 1;

      // 2. 채점 대기 우선
      if (a.pending_problems > 0 && b.pending_problems === 0) return -1;
      if (a.pending_problems === 0 && b.pending_problems > 0) return 1;

      // 3. 오답 유무 (있음 우선)
      const aHasWrong = a.wrong_problems > 0;
      const bHasWrong = b.wrong_problems > 0;
      if (aHasWrong && !bHasWrong) return -1;
      if (!aHasWrong && bHasWrong) return 1;

      // 4. 미제출 유무 (있음 우선)
      if (aHasNotSubmitted && !bHasNotSubmitted) return -1;
      if (!aHasNotSubmitted && bHasNotSubmitted) return 1;

      // 5. 페이지 번호 순
      return a.page - b.page;
    });
  };

  /**
   * 페이지 상세 보기
   */
  const handleViewPageDetail = (page: number) => {
    navigate(`/grading/${selectedStudentId}/${encodeURIComponent(selectedWorkbook!)}/page/${page}`);
  };

  /**
   * 최근 제출 카드 클릭 핸들러
   * URL 파라미터만 변경하면 useEffect가 자동으로 상태 동기화 및 탭 전환
   */
  const handleRecentCardClick = (submission: RecentPageSubmission) => {
    // URL 쿼리 파라미터 업데이트
    // useEffect가 자동으로 상태와 탭을 동기화함
    setSearchParams({
      student: submission.student_id,
      workbook: submission.workbook_name,
    });
  };

  /**
   * 문제집 할당 취소
   */
  const handleUnassignWorkbook = () => {
    console.log('handleUnassignWorkbook 실행', { selectedStudentId, selectedWorkbook });

    if (!selectedStudentId || !selectedWorkbook) {
      console.log('조건 실패');
      return;
    }

    const student = students.find(s => s.id === selectedStudentId);
    const studentName = student?.name || '선택된 학생';

    // 브라우저 기본 confirm으로 테스트
    const confirmed = window.confirm(
      `${studentName}님의 "${selectedWorkbook}" 할당을 취소하시겠습니까?\n\n학생의 제출 기록은 유지됩니다.`
    );

    if (!confirmed) {
      console.log('취소됨');
      return;
    }

    // 할당 취소 실행
    const executeUnassign = async () => {
      try {
        await assignmentService.deleteAssignment(selectedWorkbook, selectedStudentId);
        message.success('문제집 할당이 취소되었습니다');

        // 배정 목록 새로고침
        const data = await assignmentService.getStudentAssignments(selectedStudentId);
        setAssignments(data);

        // 선택된 문제집 초기화
        setSelectedWorkbook(null);
        setResult(null);
        setSearchParams({ student: selectedStudentId });
      } catch (err: any) {
        console.error('할당 취소 실패:', err);
        message.error(err.response?.data?.detail || '할당 취소에 실패했습니다');
      }
    };

    executeUnassign();
  };

  /**
   * 일괄 재채점 허가
   * 오답이 있는 모든 페이지에 대해 재채점 허가
   */
  const handleAllowRetryBulk = async () => {
    if (!selectedStudentId || !selectedWorkbook) {
      return;
    }

    try {
      const response = await allowRetryBulk(selectedStudentId, selectedWorkbook);

      if (response.success) {
        message.success(response.message);

        // 결과 새로고침
        const data = await getStudentResults(selectedStudentId, selectedWorkbook);
        setResult(data);
      } else {
        message.warning(response.message);
      }
    } catch (err: any) {
      console.error('일괄 재채점 허가 실패:', err);
      message.error(err.response?.data?.detail || '일괄 재채점 허가에 실패했습니다');
    }
  };

  /**
   * 오답이 있는 페이지 수 계산
   */
  const getPagesWithWrongCount = (): number => {
    if (!result) return 0;
    return result.pages.filter(page => page.wrong_problems > 0).length;
  };

  /**
   * 페이지 카드 렌더링
   */
  const renderPageCard = (pageResult: PageResult) => {
    return (
      <Card
        key={pageResult.page}
        hoverable
        onClick={() => handleViewPageDetail(pageResult.page)}
        style={{ cursor: 'pointer' }}
      >
        {/* 페이지 번호 & 재채점 상태 */}
        <Row>
          <Col span={24}>
            <Text strong style={{ fontSize: 16 }}>📄 {pageResult.page}페이지</Text>
            <br />
            {pageResult.retry_allowed ? (
              <Tag color="success" style={{ marginTop: 4 }}>재채점 허락됨</Tag>
            ) : pageResult.wrong_problems > 0 ? (
              <Tag color="warning" style={{ marginTop: 4 }}>재채점 대기</Tag>
            ) : (
              <Tag color="default" style={{ marginTop: 4 }}>완료</Tag>
            )}
          </Col>
        </Row>

        {/* 제출 시간 */}
        {pageResult.latest_submission_at && (
          <div style={{ marginTop: 8, marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatKoreanDateTime(pageResult.latest_submission_at)}
            </Text>
          </div>
        )}

        {/* 통계 태그 */}
        <div style={{ marginTop: 8 }}>
          {pageResult.correct_problems > 0 && (
            <Tag icon={<CheckCircleOutlined />} color="success">
              {pageResult.correct_problems}개 정답
            </Tag>
          )}
          {pageResult.wrong_problems > 0 && (
            <Tag icon={<CloseCircleOutlined />} color="error">
              {pageResult.wrong_problems}개 오답
            </Tag>
          )}
          {pageResult.pending_problems > 0 && (
            <Tag icon={<ClockCircleOutlined />} color="warning">
              {pageResult.pending_problems}개 대기
            </Tag>
          )}
          {pageResult.not_submitted_problem_numbers && pageResult.not_submitted_problem_numbers.length > 0 && (
            <Tag color="default">
              {pageResult.not_submitted_problem_numbers.length}개 미제출
            </Tag>
          )}
        </div>

        {/* 정답률 */}
        <div style={{ marginTop: 8 }}>
          <Text style={{ fontSize: 12 }}>
            정답률{' '}
            <Text
              strong
              style={{
                color:
                  pageResult.accuracy >= 80
                    ? '#52c41a'
                    : pageResult.accuracy >= 60
                    ? '#faad14'
                    : '#ff4d4f',
              }}
            >
              {pageResult.accuracy}%
            </Text>
          </Text>
        </div>
      </Card>
    );
  };

  return (
    <div>
      <Title level={2}>학생 채점 현황</Title>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as 'recent' | 'search')}
        items={[
          {
            key: 'recent',
            label: `📋 최근 제출 (${recentSubmissions.length})`,
            children: (
              <div>
                {/* 필터 및 새로고침 */}
                <Card style={{ marginBottom: 16 }}>
                  <Row gutter={16} align="middle">
                    <Col>
                      <Space>
                        <Text strong>필터:</Text>
                        <Radio.Group value={recentFilter} onChange={(e) => setRecentFilter(e.target.value)}>
                          <Radio.Button value="needs_action">조치 필요</Radio.Button>
                          <Radio.Button value="all">전체</Radio.Button>
                          <Radio.Button value="completed">완료</Radio.Button>
                        </Radio.Group>
                      </Space>
                    </Col>
                    <Col>
                      <Space>
                        <Text strong>기간:</Text>
                        <Select
                          value={recentDays}
                          onChange={(value) => setRecentDays(value)}
                          style={{ width: 90 }}
                        >
                          <Option value={1}>1일</Option>
                          <Option value={2}>2일</Option>
                          <Option value={7}>7일</Option>
                          <Option value={30}>30일</Option>
                        </Select>
                      </Space>
                    </Col>
                    <Col flex="auto" style={{ textAlign: 'right' }}>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchRecentSubmissions}
                        loading={recentLoading}
                      >
                        새로고침
                      </Button>
                    </Col>
                  </Row>
                </Card>

                {/* 로딩 */}
                {recentLoading && (
                  <div style={{ textAlign: 'center', padding: 48 }}>
                    <Spin size="large" />
                  </div>
                )}

                {/* 에러 */}
                {recentError && (
                  <Alert message="오류" description={recentError} type="error" showIcon closable />
                )}

                {/* 최근 제출 카드 리스트 */}
                {!recentLoading && !recentError && recentSubmissions.length > 0 && (
                  <Row gutter={[16, 16]}>
                    {recentSubmissions.map((submission, idx) => (
                      <Col key={idx} xs={24} sm={12} md={8} lg={6}>
                        <Card
                          hoverable
                          onClick={() => handleRecentCardClick(submission)}
                          style={{ cursor: 'pointer' }}
                        >
                          {/* 학생명 & 문제집 */}
                          <Row>
                            <Col span={24}>
                              <Text strong>{submission.student_name}</Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {submission.workbook_name}
                              </Text>
                            </Col>
                          </Row>

                          {/* 시간 */}
                          <div style={{ marginTop: 8, marginBottom: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {formatKoreanDateTime(submission.latest_submission_at)}
                            </Text>
                          </div>

                          {/* 통계 태그 */}
                          <div style={{ marginTop: 8 }}>
                            {submission.correct_count > 0 && (
                              <Tag icon={<CheckCircleOutlined />} color="success">
                                {submission.correct_count}개 정답
                              </Tag>
                            )}
                            {submission.wrong_count > 0 && (
                              <Tag icon={<CloseCircleOutlined />} color="error">
                                {submission.wrong_count}개 오답
                              </Tag>
                            )}
                            {submission.pending_count > 0 && (
                              <Tag icon={<ClockCircleOutlined />} color="warning">
                                {submission.pending_count}개 대기
                              </Tag>
                            )}
                            {submission.not_submitted_count > 0 && (
                              <Tag color="default">
                                {submission.not_submitted_count}개 미제출
                              </Tag>
                            )}
                          </div>

                          {/* 페이지 & 정답률 */}
                          <div style={{ marginTop: 8 }}>
                            <Text style={{ fontSize: 12 }}>
                              📄 {submission.page}페이지 • 정답률 {submission.accuracy}%
                            </Text>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}

                {/* 빈 상태 */}
                {!recentLoading && !recentError && recentSubmissions.length === 0 && (
                  <Card>
                    <Text type="secondary">
                      {recentFilter === 'needs_action'
                        ? '조치가 필요한 제출이 없습니다'
                        : recentFilter === 'completed'
                        ? '완료된 제출이 없습니다'
                        : `최근 ${recentDays}일간 제출이 없습니다`}
                    </Text>
                  </Card>
                )}
              </div>
            ),
          },
          {
            key: 'search',
            label: '🔍 학생별 조회',
            children: (
              <div>
                {/* 학생 및 문제집 선택 */}
                <Card style={{ marginBottom: 24 }}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <div style={{ marginBottom: 8 }}>
                        <UserOutlined /> <Text strong>학생 선택</Text>
                      </div>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="학생을 선택하세요"
                        value={selectedStudentId}
                        onChange={(value) => {
                          setSelectedStudentId(value);
                          setSearchParams({ student: value });
                        }}
                        showSearch
                        optionFilterProp="children"
                      >
                        {students.map((student) => (
                          <Option key={student.id} value={student.id}>
                            {student.name} ({student.email})
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: 8 }}>
                        <BookOutlined /> <Text strong>문제집 선택</Text>
                      </div>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="문제집을 선택하세요"
                        value={selectedWorkbook}
                        onChange={(value) => {
                          setSelectedWorkbook(value);
                          if (selectedStudentId) {
                            setSearchParams({ student: selectedStudentId, workbook: value });
                          }
                        }}
                        disabled={!selectedStudentId || assignments.length === 0}
                        showSearch
                        optionFilterProp="children"
                      >
                        {assignments.map((assignment) => (
                          <Option key={assignment.workbook_name} value={assignment.workbook_name}>
                            {assignment.workbook_name}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                  </Row>
                </Card>

                {/* 결과 표시 */}
                {loading && (
                  <div style={{ textAlign: 'center', padding: 48 }}>
                    <Spin size="large" />
                  </div>
                )}

                {error && (
                  <Alert message="오류" description={error} type="error" showIcon closable />
                )}

                {result && (
                  <>
                    {/* 전체 통계 */}
                    <Card
                      style={{ marginBottom: 24 }}
                      extra={
                        <Space>
                          {/* 일괄 재채점 허가 버튼: 오답이 있는 페이지가 1개 이상일 때만 표시 */}
                          {getPagesWithWrongCount() > 0 && (
                            <Button
                              type="primary"
                              icon={<ReloadOutlined />}
                              onClick={handleAllowRetryBulk}
                            >
                              일괄 재채점 허가 ({getPagesWithWrongCount()}개 페이지)
                            </Button>
                          )}
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              console.log('버튼 클릭됨!');
                              handleUnassignWorkbook();
                            }}
                          >
                            문제집 할당 취소
                          </Button>
                        </Space>
                      }
                    >
                      <Title level={4}>{result.student_name}님의 학습 현황</Title>
                      <Text type="secondary">문제집: {result.workbook_name}</Text>
                    </Card>

                    {/* 페이지 필터 */}
                    <Card style={{ marginBottom: 16 }}>
                      <Row gutter={16} align="middle">
                        <Col>
                          <Space>
                            <Text strong>보기:</Text>
                            <Radio.Group value={filter} onChange={(e) => setFilter(e.target.value)}>
                              <Radio.Button value="needs_attention">조치 필요</Radio.Button>
                              <Radio.Button value="all">전체 보기</Radio.Button>
                            </Radio.Group>
                          </Space>
                        </Col>
                        <Col flex="auto">
                          <Input
                            placeholder="페이지 번호 검색 (예: 11)"
                            value={pageSearch}
                            onChange={(e) => setPageSearch(e.target.value)}
                            style={{ width: 200 }}
                            allowClear
                          />
                        </Col>
                      </Row>
                    </Card>

                    {/* 페이지별 결과 카드 */}
                    <Title level={4} style={{ marginBottom: 16 }}>
                      페이지별 결과 ({getFilteredAndSortedPages().length}개)
                    </Title>
                    {getFilteredAndSortedPages().length > 0 ? (
                      <Row gutter={[16, 16]}>
                        {getFilteredAndSortedPages().map((pageResult) => (
                          <Col key={pageResult.page} xs={24} sm={12} md={8} lg={6}>
                            {renderPageCard(pageResult)}
                          </Col>
                        ))}
                      </Row>
                    ) : (
                      <Card>
                        <Text type="secondary">
                          {filter === 'needs_attention'
                            ? '조치가 필요한 페이지가 없습니다'
                            : '제출한 페이지가 없습니다'}
                        </Text>
                      </Card>
                    )}
                  </>
                )}

                {!loading && !error && !result && selectedStudentId && selectedWorkbook && (
                  <Card>
                    <Text type="secondary">결과를 불러오는 중...</Text>
                  </Card>
                )}

                {!selectedStudentId && !selectedWorkbook && (
                  <Card>
                    <Text type="secondary">학생과 문제집을 선택하세요</Text>
                  </Card>
                )}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
